import _ from "lodash"
import store from "../app/Store"
import {
  INotification,
  NotificationKind,
  notify
} from "../containers/notificationSlice"
import { handleFailure } from "../pages/Support"

const __SERVICE__ = "/api"

// This enum must match backend enum Code in ApiError.cs
export enum JsonErrorCode {
  ParseError = -32700, // Invalid JSON was received by the server. An error occurred on the server while parsing the JSON text.
  InvalidRequest = -32600, // The JSON sent is not a valid Request object.
  MethodNotFound = -32601, // The method does not exist / is not available.
  InvalidParams = -32602, // Invalid method parameter(s).
  InternalError = -32603, // Internal JSON-RPC error.

  // -32000 to -32099	Server error	Reserved for implementation-defined server-errors.
  AuthenticationError = -32000, // Request requires authentication
  AuthorizationError = -32001, // User is not authorized for this operation
  NoData = -32002, // Request assumed data which were not present
  Restricted = -32003, // Request for permanently or temporarily restricted resource
  DownloadFailed = -32004, // Download request to coordinator failed
  ApplicationError = -32005, // Internal application error
  UserError = -32006, // Unspecified error caused by eg. invalid parameters
  RequestFailed = -32007 // Request to coordinator failed
}

export interface IErrorObject {
  code: JsonErrorCode
  message: string
  kind: NotificationKind
}

const showNoMsgMethods = [
  "login1",
  "login2",
  "loginReplace",
  "loginUnlock",
  "requestUserPermissions"
]

// callback may be invoked without parameter: https://www.typescriptlang.org/docs/handbook/2/functions.html#optional-parameters-in-callbacks
// if invoked with parameters, implementations () => {} will just ignore the parameter: https://stackoverflow.com/a/12694124
export type OnFailure = (error?: IErrorObject) => void

// variant without onFailure callback
export async function callBackend1(
  method: string,
  pageId: string,
  jsonParams: any
): Promise<any> {
  const onFailure = () => {}
  return callBackend(method, pageId, jsonParams, onFailure)
}

export default async function callBackend(
  method: string,
  //method is NOT CRUD
  pageId: string,
  jsonParams: any,
  onFailure: OnFailure
): Promise<any> {
  // https://jasonwatmore.com/post/2020/02/01/react-fetch-http-post-request-examples
  const endpoint = __SERVICE__ + "/" + method // https://create-react-app.dev/docs/proxying-api-requests-in-development/
  const httpRequest = makeRequest(method, pageId, jsonParams)

  let n: INotification
  try {
    const httpResponse = await fetch(endpoint, httpRequest)

    let jsonInternalMessage = ""
    let jsonErrorCode = JsonErrorCode.InternalError

    if (!httpResponse.ok) {
      n = getHttpErrorNotification(httpResponse)
    } else {
      const jsonResponse = await httpResponse.json()
      if (jsonResponse.result) {
        let msg = jsonResponse.result.message
        if (msg && !showNoMsgMethods.includes(method)) {
          n = {
            kind: NotificationKind.INFO,
            title: "Information",
            message: msg
          }
          store.dispatch(notify(n))
        }
        return jsonResponse.result
      }
	    console.error(`Backend error in ${method}:`, jsonResponse.error.message);
      n = getJsonErrorNotification(jsonResponse, method)
      handleFailure(jsonResponse.error.code)
      jsonInternalMessage = jsonResponse.error.data
      jsonErrorCode = jsonResponse.error.code
    }

    const error: IErrorObject = {
      code: jsonErrorCode,
      message: n.message,
      kind: n.kind
    }
    onFailure(error)

    const msg = `${method}: ${n.message}`
    if (n.kind === NotificationKind.ERROR) {
      console.error(msg, httpRequest, jsonInternalMessage)
    } else {
      // eslint-disable-next-line no-console
      console.warn(msg, httpRequest, jsonInternalMessage)
    }

    store.dispatch(notify(n))
  } catch (err) {
    const msg = `${method}: Problem handling response: ${err.message}`
    console.error(msg, httpRequest)
  }
}

const makeRequest = (
  method: string,
  pageId: string,
  jsonParams: any
): RequestInit => {
  let bodyObj = {
      jsonrpc: "2.0",
      id: 42,
      method: method,
      params: _.assignIn(jsonParams, { pageId: pageId })
    
  }
  let headers: Headers = new Headers()
  headers.append("Content-Type", "application/json; charset=UTF-8")
  // headers.append("upn", "mmi@pdc.com") // TODO: test only - delete me

  return {
    method: "POST",
    headers: headers,
    // mode: "cors", // no-cors, *cors, same-origin
    credentials: "include", // default
    cache: "no-cache",
    redirect: "follow",
    referrer: "no-referrer",
    body: JSON.stringify(bodyObj)
  }
}

function getHttpErrorNotification(httpResponse: Response) {
  const n: INotification = {
    kind: NotificationKind.ERROR,
    title: "Connection failure",
    message: `${httpResponse.statusText} (${httpResponse.status})`
  }

  if (httpResponse.status === 401) {
    n.kind = NotificationKind.WARNING
    n.title = "Not Authorized"
    n.message = "You have been logged out.\r\nPlease login to continue"
    n.code = JsonErrorCode.AuthenticationError
  }

  return n
}

function getJsonErrorNotification(
  jsonResp: any,
  method: string
): INotification {
  const n: INotification = {
    kind: undefined,
    title: undefined,
    message: undefined,
    code: undefined
  }

  const jsonError = jsonResp.error
  if (!jsonError) {
    // response object not wellformed
    n.kind = NotificationKind.ERROR
    n.title = `Internal error: ${method}`
    n.message = `Malformed JsonRpc response:\r\n${jsonResp}`
    return n
  }

  const code = jsonError.code
  n.code = code
  if (code === JsonErrorCode.UserError) {
    // user error
    n.kind = NotificationKind.WARNING
    n.title = "Input error"
    n.message = jsonError.message
  } else if (code === JsonErrorCode.Restricted) {
    n.kind = NotificationKind.WARNING
    n.title = "Restricted"
    n.message = jsonError.message
  } else if (code === JsonErrorCode.RequestFailed) {
    n.kind = NotificationKind.WARNING
    n.title = "Request failed"
    n.message = jsonError.message
  } else {
    // session timeout, logged out by other session, internal error or abuse
    if (code === JsonErrorCode.AuthenticationError) {
      n.kind = NotificationKind.WARNING
      n.title = "Not Authenticated"
      n.message = "Your session timed out"
    } else if (code === JsonErrorCode.AuthorizationError) {
      n.kind = NotificationKind.ERROR
      n.title = "Not Authorized"
      n.message = jsonError.message
    } else {
      n.kind = NotificationKind.ERROR
      n.title = "Internal error"
      n.message = "Internal Error.  Please try again later or contact us."
    }
  }

  return n
}

export async function getAppFileVersion() {
  const url = "app.json" // relative to index.html
  // avoid caching of file: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control#request_directives
  const httpRequest: RequestInit = { cache: "no-store" } // no-cache was not enough
  const httpResponse = await fetch(url, httpRequest)
  if (httpResponse.status === 200) {
    const json = await httpResponse.json()
    return json?.version
  } else {
    return null
  }
}
